import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AdminLayout, { type NavItem } from '../../components/admin/AdminLayout';
import PassBuyersModal from '../../components/admin/PassBuyersModal';
import { TOUR, tourAttr } from '../../features/hostTour';
import { useAuth } from '../../hooks/useAuth';
import { bilingualLines, localize, toScript } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { passApi, type AdminPassListItem } from '../../services/passApi';

const hostNavItems: NavItem[] = [
  { to: '/host/activities', label: 'Activities' },
  { to: '/host/courses', label: 'Courses' },
  { to: '/host/passes', label: 'Passes' },
  { to: '/host/bookings', label: 'Bookings' },
  { to: '/host/email', label: 'Email' },
];

type FilterType = 'all' | 'active' | 'paused' | 'pending' | 'expired' | 'sold_out';

function getPassDisplayStatus(pass: AdminPassListItem) {
  const now = new Date();
  const expired = new Date(pass.expiresAt) < now;
  const soldOut = pass.soldCount >= pass.maxSales;

  if (expired) return 'expired';
  if (soldOut) return 'sold_out';
  if (pass.status === 'PENDING_APPROVAL') return 'pending';
  if (pass.status === 'PAUSED') return 'paused';
  return 'active';
}

export default function AdminPasses() {
  const siteLanguage = useSiteLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();
  const isHostContext = location.pathname.startsWith('/host/');
  const basePath = isHostContext ? '/host/passes' : '/admin/passes';

  const [passes, setPasses] = useState<AdminPassListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('active');
  const [selectedPass, setSelectedPass] = useState<AdminPassListItem | null>(null);
  const [viewingBuyersPass, setViewingBuyersPass] = useState<AdminPassListItem | null>(null);
  const [togglingStatus, setTogglingStatus] = useState<string | null>(null);
  const [deletingPass, setDeletingPass] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    const fetchPasses = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        const token = await getToken();
        if (!token) throw new Error('No access token');
        const data = await passApi.getAdminPasses(token);
        setPasses(data);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load passes';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchPasses();
  }, [isAuthenticated, getToken]);

  const handleToggleStatus = async (pass: AdminPassListItem) => {
    const newStatus = pass.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE';
    try {
      setTogglingStatus(pass.id);
      const token = await getToken();
      if (!token) throw new Error('No access token');
      await passApi.updatePassStatus(pass.id, newStatus, token);
      setPasses((prev) => prev.map((p) => (p.id === pass.id ? { ...p, status: newStatus } : p)));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update status';
      setError(errorMessage);
    } finally {
      setTogglingStatus(null);
    }
  };

  const handleDelete = async (pass: AdminPassListItem) => {
    if (
      !confirm(
        `Delete "${localize(pass.titleCN, pass.titleEN, siteLanguage)}"? This action cannot be undone.`
      )
    )
      return;

    try {
      setDeletingPass(pass.id);
      const token = await getToken();
      if (!token) throw new Error('No access token');
      await passApi.deletePass(pass.id, token);
      setPasses((prev) => prev.filter((p) => p.id !== pass.id));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete pass';
      setError(errorMessage);
    } finally {
      setDeletingPass(null);
    }
  };

  const filteredPasses = passes.filter((pass) => {
    if (filter === 'all') return true;
    return getPassDisplayStatus(pass) === filter;
  });

  const getFilterCount = (f: FilterType) => {
    if (f === 'all') return passes.length;
    return passes.filter((p) => getPassDisplayStatus(p) === f).length;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading passes...</p>
        </div>
      </div>
    );
  }

  return (
    <AdminLayout
      title="Pass Management"
      navItems={isHostContext ? hostNavItems : undefined}
      headerRight={
        <Link
          to={`${basePath}/new`}
          className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          {...tourAttr(TOUR.passesCreate)}
        >
          <span className="mr-2">+</span>
          Create Pass
        </Link>
      }
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div>
          <nav className="flex -mb-px overflow-x-auto" {...tourAttr(TOUR.passesTabs)}>
            {(['active', 'pending', 'paused', 'expired', 'sold_out', 'all'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                data-tour={
                  tab === 'pending'
                    ? TOUR.passesTabPending
                    : tab === 'active'
                      ? TOUR.passesTabActive
                      : undefined
                }
                className={`px-6 py-3 text-sm font-medium border-b-2 whitespace-nowrap flex-shrink-0 ${
                  filter === tab
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab === 'sold_out'
                  ? 'Sold Out'
                  : tab === 'pending'
                    ? 'Pending Review'
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                  {getFilterCount(tab)}
                </span>
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Passes Grid */}
      {filteredPasses.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <div className="text-gray-400 text-5xl mb-4">🎫</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No passes found</h3>
          <p className="text-gray-500 mb-6">
            {filter === 'all'
              ? 'Create your first pass to get started.'
              : `No ${filter === 'sold_out' ? 'sold out' : filter} passes.`}
          </p>
          {filter === 'all' && (
            <Link
              to={`${basePath}/new`}
              className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Create Pass
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPasses.map((pass) => (
            <PassCard
              key={pass.id}
              pass={pass}
              onEdit={() => navigate(`${basePath}/${pass.id}/edit`)}
              onToggleStatus={() => handleToggleStatus(pass)}
              onDelete={() => handleDelete(pass)}
              onClick={() => setSelectedPass(pass)}
              onViewBuyers={() => setViewingBuyersPass(pass)}
              isToggling={togglingStatus === pass.id}
              isDeleting={deletingPass === pass.id}
              isHostContext={isHostContext}
            />
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {selectedPass && (
        <PassDetailModal pass={selectedPass} onClose={() => setSelectedPass(null)} />
      )}

      {/* Buyers Modal */}
      {viewingBuyersPass && (
        <PassBuyersModal
          passId={viewingBuyersPass.id}
          passTitle={localize(viewingBuyersPass.titleCN, viewingBuyersPass.titleEN, siteLanguage)}
          getToken={getToken}
          onClose={() => setViewingBuyersPass(null)}
          isHostContext={isHostContext}
        />
      )}
    </AdminLayout>
  );
}

function PassCard({
  pass,
  onEdit,
  onToggleStatus,
  onDelete,
  onClick,
  onViewBuyers,
  isToggling,
  isDeleting,
  isHostContext,
}: {
  pass: AdminPassListItem;
  onEdit: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
  onClick: () => void;
  onViewBuyers: () => void;
  isToggling: boolean;
  isDeleting: boolean;
  isHostContext: boolean;
}) {
  const siteLanguage = useSiteLanguage();
  const displayStatus = getPassDisplayStatus(pass);

  const formatSessionDateTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const date = start.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
    const formatTime = (d: Date) => {
      const h = d.getHours();
      const m = d.getMinutes();
      const period = h >= 12 ? 'pm' : 'am';
      const hour = h % 12 || 12;
      return m === 0 ? `${hour}${period}` : `${hour}:${m.toString().padStart(2, '0')}${period}`;
    };
    return `${date} ${formatTime(start)}-${formatTime(end)}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden" {...tourAttr(TOUR.passCard)}>
      {/* Top section with activity info and status */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <p className="text-sm text-gray-500 line-clamp-1">
            {localize(pass.activity.titleCN, pass.activity.titleEN, siteLanguage)}
          </p>
          <PassStatusBadge status={displayStatus} />
        </div>
        <h3 className="font-semibold text-gray-900 line-clamp-1">
          {bilingualLines(pass.titleCN, pass.titleEN, siteLanguage)[0]}
        </h3>
        {bilingualLines(pass.titleCN, pass.titleEN, siteLanguage)[1] && (
          <p className="text-sm text-gray-500 line-clamp-1">
            {bilingualLines(pass.titleCN, pass.titleEN, siteLanguage)[1]}
          </p>
        )}
      </div>

      {/* Details */}
      <div className="p-4">
        <div className="grid grid-cols-3 gap-3 text-sm mb-3">
          <div>
            <p className="text-gray-500">Price</p>
            <p className="font-medium">${(pass.price / 100).toFixed(2)}</p>
          </div>
          <div>
            <p className="text-gray-500">Sessions</p>
            <p className="font-medium">{pass.totalSessions}x</p>
          </div>
          <div>
            <p className="text-gray-500">Sold</p>
            <p className="font-medium">
              {pass.soldCount}/{pass.maxSales}
            </p>
          </div>
        </div>

        <div className="text-sm text-gray-500 mb-1">
          Expires:{' '}
          {new Date(pass.expiresAt).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </div>

        {pass.host && (
          <div className="text-sm text-gray-500 mb-1">Host: {pass.host.displayName}</div>
        )}

        <div className="text-sm text-gray-500">
          {pass.sessions.length} session slot{pass.sessions.length !== 1 ? 's' : ''} ·{' '}
          {pass._count.userPasses > 0 ? (
            <button onClick={onViewBuyers} className="text-blue-600 hover:underline">
              {pass._count.userPasses} buyer{pass._count.userPasses !== 1 ? 's' : ''}
            </button>
          ) : (
            <span>
              {pass._count.userPasses} buyer{pass._count.userPasses !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Expandable sessions preview */}
        {pass.sessions.length > 0 && (
          <div className="mt-3 space-y-1 max-h-24 overflow-y-auto">
            {pass.sessions.slice(0, 3).map((ps) => (
              <div
                key={ps.session.id}
                className="text-xs bg-gray-50 px-2 py-1 rounded text-gray-700"
              >
                {formatSessionDateTime(ps.session.startTime, ps.session.endTime)}
              </div>
            ))}
            {pass.sessions.length > 3 && (
              <div className="text-xs text-gray-400 px-2">+{pass.sessions.length - 3} more</div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 mt-4" {...tourAttr(TOUR.passCardActions)}>
          <button
            onClick={onEdit}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Edit
          </button>
          <button
            onClick={onClick}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            View
          </button>
          {displayStatus !== 'expired' &&
            displayStatus !== 'sold_out' &&
            !(isHostContext && pass.status === 'PENDING_APPROVAL') && (
              <button
                onClick={onToggleStatus}
                disabled={isToggling}
                className={`px-3 py-2 text-sm border rounded-lg transition-colors ${
                  pass.status === 'ACTIVE'
                    ? 'text-yellow-600 border-yellow-200 hover:bg-yellow-50'
                    : pass.status === 'PENDING_APPROVAL'
                      ? 'text-blue-600 border-blue-200 hover:bg-blue-50'
                      : 'text-green-600 border-green-200 hover:bg-green-50'
                } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isToggling
                  ? '...'
                  : pass.status === 'ACTIVE'
                    ? 'Pause'
                    : pass.status === 'PENDING_APPROVAL'
                      ? 'Approve'
                      : 'Activate'}
              </button>
            )}
          {pass.soldCount === 0 && (
            <button
              onClick={onDelete}
              disabled={isDeleting}
              className={`px-3 py-2 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors ${
                isDeleting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isDeleting ? '...' : 'Delete'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PassStatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: 'bg-green-100', text: 'text-green-800', label: 'Active' },
    pending: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Pending Review' },
    paused: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Paused' },
    expired: { bg: 'bg-red-100', text: 'text-red-800', label: 'Expired' },
    sold_out: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Sold Out' },
  };

  const config = statusConfig[status] || statusConfig.active;

  return (
    <span
      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

function PassDetailModal({ pass, onClose }: { pass: AdminPassListItem; onClose: () => void }) {
  const siteLanguage = useSiteLanguage();
  const displayStatus = getPassDisplayStatus(pass);

  const formatSessionDateTime = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const date = start.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    const formatTime = (d: Date) => {
      const h = d.getHours();
      const m = d.getMinutes();
      const period = h >= 12 ? 'pm' : 'am';
      const hour = h % 12 || 12;
      return m === 0 ? `${hour}${period}` : `${hour}:${m.toString().padStart(2, '0')}${period}`;
    };
    return `${date} ${formatTime(start)}-${formatTime(end)}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">Pass Details</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
              ✕
            </button>
          </div>

          {/* Info grid */}
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-500">Title</label>
              <p className="text-gray-900">
                {bilingualLines(pass.titleCN, pass.titleEN, siteLanguage)[0]}
              </p>
              {bilingualLines(pass.titleCN, pass.titleEN, siteLanguage)[1] && (
                <p className="text-sm text-gray-500">
                  {bilingualLines(pass.titleCN, pass.titleEN, siteLanguage)[1]}
                </p>
              )}
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Activity</label>
              <p className="text-gray-900">
                <Link
                  to={`/activity/${pass.activity.slug}`}
                  target="_blank"
                  className="text-primary hover:underline"
                >
                  {localize(pass.activity.titleCN, pass.activity.titleEN, siteLanguage)}
                </Link>
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Price</label>
                <p className="text-gray-900">${(pass.price / 100).toFixed(2)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Total Sessions</label>
                <p className="text-gray-900">{pass.totalSessions}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Sold / Max</label>
                <p className="text-gray-900">
                  {pass.soldCount} / {pass.maxSales}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">
                  <PassStatusBadge status={displayStatus} />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Expires</label>
                <p className="text-gray-900">
                  {new Date(pass.expiresAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Buyers</label>
                <p className="text-gray-900">{pass._count.userPasses}</p>
              </div>
            </div>

            {pass.host && (
              <div>
                <label className="text-sm font-medium text-gray-500">Host</label>
                <p className="text-gray-900">{pass.host.displayName}</p>
              </div>
            )}

            {pass.descriptionCN && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Description (CN){pass.descriptionAutoLang === 'ZH' ? ' · Auto-translated' : ''}
                </label>
                <p className="text-gray-900 whitespace-pre-wrap">
                  {toScript(pass.descriptionCN, siteLanguage)}
                </p>
              </div>
            )}
            {pass.descriptionEN && (
              <div>
                <label className="text-sm font-medium text-gray-500">
                  Description (EN){pass.descriptionAutoLang === 'EN' ? ' · Auto-translated' : ''}
                </label>
                <p className="text-gray-900 whitespace-pre-wrap">{pass.descriptionEN}</p>
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-gray-500">Slug</label>
              <p className="text-gray-900 font-mono text-sm">{pass.slug}</p>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-500">Created</label>
              <p className="text-gray-900">
                {new Date(pass.createdAt).toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>

            {/* Sessions list */}
            {pass.sessions.length > 0 && (
              <div>
                <label className="text-sm font-medium text-gray-500 mb-2 block">
                  Available Sessions ({pass.sessions.length})
                </label>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {pass.sessions.map((ps) => (
                    <div
                      key={ps.session.id}
                      className="text-sm bg-gray-50 px-3 py-2 rounded text-gray-700"
                    >
                      {formatSessionDateTime(ps.session.startTime, ps.session.endTime)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
